import { createFileRoute } from '@tanstack/react-router';
import request from '@nineeng/request';
import { useState } from 'react';
import { isEmail } from 'validator';
import TextField from '@mui/material/TextField';

import { Logo, LoadingButton } from '@repo/components';
import { _deprecatedCardContent as CardContent, Card } from '@repo/components';

interface ForgotPasswordFormProps {
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	email: string;
	error: string | null;
	sendEmail: (e: React.FormEvent) => void | Promise<void>;
	loading: boolean;
}

const ForgotPasswordForm = ({ onChange, email, error, sendEmail, loading }: ForgotPasswordFormProps) => (
	<form onSubmit={sendEmail}>
		<CardContent>
			<TextField
				id="email"
				name="email"
				required
				label="Email"
				fullWidth={true}
				autoFocus={true}
				value={email}
				onChange={onChange}
				error={!!error}
				helperText={error}
			/>

			<LoadingButton
				color="secondary"
				variant="outlined"
				type="submit"
				fullWidth={true}
				onClick={sendEmail}
				loading={loading}
			>
				send password reset
			</LoadingButton>
		</CardContent>
	</form>
);

interface ForgotPasswordSearch {
	email?: string;
}

export const Route = createFileRoute('/authentication/forgot-password')({
	validateSearch: (search: Record<string, unknown>): ForgotPasswordSearch => {
		return { email: (search.email as string) || '' };
	},
	component: ForgotPassword,
});

function ForgotPassword() {
	const { email: initialEmail } = Route.useSearch();
	const [state, setState] = useState<'init' | 'submitted'>('init');
	const [loading, setLoading] = useState(false);
	const [email, setEmail] = useState(initialEmail);
	const [error, setError] = useState<string | null>(null);

	const handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => setEmail(target.value.trim());

	const sendEmail = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!isEmail(email)) {
			return setError('Please enter a valid email');
		}

		setError(null);

		try {
			setLoading(true);
			await request.post('/api/auth/reset-password-request', { email });
			setState('submitted');
		} catch (err) {
			setState('init');
			const error = err as Error;
			setError(error.message);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="bg-background flex flex-col h-screen">
			<div className="max-w-[600px] mx-auto h-full flex flex-col justify-center gap-4 p-8 pt-4 w-full items-center">
				<Logo variant="tagline" className="w-[400px] mb-8" />
				<Card>
					{state === 'init' && (
						<ForgotPasswordForm
							sendEmail={sendEmail}
							onChange={handleChange}
							error={error}
							loading={loading}
							email={email}
						/>
					)}
					{state === 'submitted' && (
						<CardContent>
							If the given email corresponds with an account, you should receive a message in the next few minutes.
						</CardContent>
					)}
				</Card>
			</div>
		</div>
	);
}
